import {
  AnyAction,
  createListenerMiddleware,
  Dispatch,
  ListenerEffectAPI,
  ThunkDispatch,
} from "@reduxjs/toolkit"
import * as Sentry from "@sentry/react"
import {
  addToSynced,
  addToSyncQueue,
  clearSyncedAndFailedRooms,
  markAsFailed,
} from "../reducers/roomSyncStatusSlice"
import {
  lastSyncedAtIsFarBehind,
  syncSingleRoom,
} from "../routes/archiveLoader"
import { resolveDriverJidForChat } from "../lib/resolveDriverForChat"
import XmppVCardApi from "../api/xmppVCardApi"
import { upsertProfile } from "../reducers/profilesSlice"
import { AppDispatch, RootState } from "../reducers/store"
import { addOrUpdateParticipant, Chat } from "../reducers/chatsSlice"
import { retry } from "../utils"
import RoomSyncQueue from "../lib/roomSyncQueue"
import {
  initSyncMetadata,
  isInitSyncDone,
} from "../reducers/roomSyncStatusSelectors"
import { selectLastSyncedAt } from "../reducers/chatMetaSlice"
import { addToSeenChats } from "../reducers/seenChatsSlice"
import { Client } from "@xmpp/client"
import { iterateThroughArchivePages, MAMQueryOptions } from "../api/xeps/MAM"
import MAMStanzaProcessor from "../api/xeps/MAMStanzaProcessor"
import {
  DeliveryOrder,
  DocumentDriverContext,
  DocumentLink,
  ExtractedMetadata,
  Fasten,
  Marker,
  Message,
  MessageFileShared,
  MessageRetraction,
  processArchivedBatch,
  Reaction,
} from "../reducers/messagesSlice"
import { captureError } from "../ErrorHandlers"
import { subMilliseconds } from "date-fns"
import { FULL_ROOM_SYNC_DONE } from "../stream/pubSubSync"

type ProcessDriver = {
  roomJid: string
  getState: () => RootState
  dispatch: Dispatch
}

const processVcards = async ({
  roomJid,
  getState,
  dispatch,
}: ProcessDriver) => {
  const state = getState()
  const accountChat = state.accountChats[roomJid]
  const jid = resolveDriverJidForChat(accountChat, roomJid)
  if (!jid) return

  const vcard = await XmppVCardApi.fetchVCard(window.XMPP!, jid)
  if (!vcard) return

  dispatch(upsertProfile({ profile: vcard }))
  dispatch(addOrUpdateParticipant({ chatJid: roomJid, participant: vcard }))

  return vcard
}

const processDriver = async ({
  roomJid,
  getState,
  dispatch,
}: ProcessDriver) => {
  const state = getState()
  const chat = state.chats.find((c) => c.jid === roomJid)
  if (!chat) return
  await processVcards({ roomJid, getState, dispatch })
}

const addAllRoomsToSeen = (
  listenerApi: ListenerEffectAPI<
    unknown,
    ThunkDispatch<unknown, unknown, AnyAction>,
    unknown
  >,
) => {
  const state = listenerApi.getState() as RootState
  listenerApi.dispatch(addToSeenChats(state.chats.map((c) => c.jid)))
}
const ROOM_SYNC_RETRIES = 3
const JOIN_ROOM_TIMEOUT = 6000
let initSyncReported = false

/**
 * Determines whether a room sync error is worth retrying.
 * Stanza errors (e.g. not-allowed, forbidden, item-not-found) are permanent
 * and should fail immediately. Timeouts and network errors are transient
 * and should be retried.
 */
const isRetryableRoomSyncError = (error: unknown): boolean => {
  if (
    error !== null &&
    typeof error === "object" &&
    "condition" in error &&
    typeof (error as { condition: unknown }).condition === "string"
  ) {
    return false
  }
  return true
}

const reportFullSync = (
  listenerApi: ListenerEffectAPI<
    unknown,
    ThunkDispatch<unknown, unknown, AnyAction>,
    unknown
  >,
) => {
  const state = listenerApi.getState() as RootState
  const meta = initSyncMetadata(state)
  window.analytics.track("InitialRoomSync", meta)
  console.log("InitialRoomSync", meta)
}

const processSubscriptions = async (
  action: { payload: { jid: string } },
  listenerApi: ListenerEffectAPI<
    unknown,
    ThunkDispatch<unknown, unknown, AnyAction>,
    unknown
  >,
) => {
  await retry(
    processDriver,
    [
      {
        roomJid: action.payload.jid,
        getState: listenerApi.getState as () => RootState,
        dispatch: listenerApi.dispatch,
      },
    ],
    { maxAttempts: ROOM_SYNC_RETRIES, backoff: true },
  )
}

/**
 * Extracts a human-readable reason from a room sync error.
 * XMPP StanzaError already formats `.message` as "condition - text"
 * (e.g. "forbidden - Only members may query archives of this room").
 */
const extractFailureReason = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === "string") {
    return error
  }

  return undefined
}

const processRoomSyncEffect = async (
  action: { payload: { jid: string } },
  listenerApi: ListenerEffectAPI<
    unknown,
    ThunkDispatch<unknown, unknown, AnyAction>,
    unknown
  >,
) => {
  const syncStart = new Date()
  try {
    const isFullSyncing = lastSyncedAtIsFarBehind(
      selectLastSyncedAt(action.payload.jid)(
        listenerApi.getState() as RootState,
      ),
      listenerApi.getState() as RootState,
    )

    await retry(
      syncSingleRoom,
      [
        action.payload.jid,
        window.XMPP!,
        listenerApi.getState() as RootState,
        listenerApi.dispatch,
      ],
      {
        maxAttempts: ROOM_SYNC_RETRIES,
        backoff: true,
        shouldRetry: isRetryableRoomSyncError,
      },
    )
    await retry(
      window.mucPlugin.joinRoom,
      [action.payload.jid, window.myJID.local, syncStart, JOIN_ROOM_TIMEOUT],
      {
        maxAttempts: ROOM_SYNC_RETRIES,
        backoff: true,
        shouldRetry: isRetryableRoomSyncError,
      },
    )
    RoomSyncQueue.enqueue(
      () =>
        processSubscriptions(action, listenerApi).catch((e) => {
          captureError(e, {
            origin: "InitialLoad",
            extra: { message: "processSubscriptions", jid: action.payload.jid },
          })
        }),
      `${action.payload.jid}#subscriptions`,
    )
    listenerApi.dispatch(
      addToSynced({ jid: action.payload.jid, wasFullSynced: isFullSyncing }),
    )
  } catch (e) {
    const reason = extractFailureReason(e)
    console.error("Error syncing room", action.payload.jid, reason ?? e)
    listenerApi.dispatch(markAsFailed({ jid: action.payload.jid, reason }))
  } finally {
    const state = listenerApi.getState() as RootState
    if (!initSyncReported && isInitSyncDone(state)) {
      initSyncReported = true
      reportFullSync(listenerApi)
      addAllRoomsToSeen(listenerApi)
      window.XMPP!.emit(FULL_ROOM_SYNC_DONE)
    }
  }
}

export async function fetchMessagesFromPast(
  chat: Chat,
  missingPinedMessageId: number,
  client: Client,
  myJid: string,
  dispatch: AppDispatch,
): Promise<Message[]> {
  let options: MAMQueryOptions = {
    afterStamp: `${subMilliseconds(new Date(missingPinedMessageId / 1000), 1).toISOString()}`,
  }

  if (chat.room) {
    options = {
      ...options,
      toJid: chat.jid,
    }
  } else {
    options = {
      ...options,
      withJid: chat.jid,
    }
  }

  const markersAccumulator: Marker[][] = []
  const messagesAccumulator: Message[][] = []
  const reactionsAccumulator: Reaction[][] = []
  const messageFileSharesAccumulator: MessageFileShared[][] = []
  const messageRetractionsAccumulator: MessageRetraction[][] = []
  const extractedMetadataAccumulator: ExtractedMetadata[][] = []
  const documentDriverContextAccumulator: DocumentDriverContext[][] = []
  const deliveryOrdersAccumulator: DeliveryOrder[][] = []
  const documentLinksAccumulator: DocumentLink[][] = []
  const routeUpdatesAccumulator: Fasten[][] = []

  try {
    await iterateThroughArchivePages(
      client,
      options,
      MAMStanzaProcessor.accumulatePages(
        messagesAccumulator,
        markersAccumulator,
        reactionsAccumulator,
        messageFileSharesAccumulator,
        messageRetractionsAccumulator,
        extractedMetadataAccumulator,
        documentDriverContextAccumulator,
        deliveryOrdersAccumulator,
        documentLinksAccumulator,
        routeUpdatesAccumulator,
        client,
      ),
      "asc",
      500,
    )

    // Process the accumulated data and dispatch a single action
    const accumulatedData = {
      messagesAccumulator,
      markersAccumulator,
      reactionsAccumulator,
      messageFileSharesAccumulator,
      messageRetractionsAccumulator,
      extractedMetadataAccumulator,
      documentDriverContextAccumulator,
      deliveryOrdersAccumulator,
      documentLinksAccumulator,
      routeUpdatesAccumulator,
    }

    const payload = MAMStanzaProcessor.processAccumulatedData(
      accumulatedData,
      "asc",
      false,
      client.jid!.toString(),
      "append",
    )

    dispatch(processArchivedBatch(payload))

    return payload.messages
  } catch (error) {
    captureError(error, {
      origin: "UserAction",
      extra: { message: "fetchMessagesFromPast" },
    })
    return []
  }
}

const roomSyncListener = createListenerMiddleware()

roomSyncListener.startListening({
  actionCreator: clearSyncedAndFailedRooms,
  effect: () => {
    initSyncReported = false
  },
})

roomSyncListener.startListening({
  actionCreator: addToSyncQueue,
  effect: async (action, listenerApi) => {
    const process = async () => await processRoomSyncEffect(action, listenerApi)
    const spanOpts = {
      name: "RoomSync",
      attributes: { jid: action.payload.jid },
    }
    RoomSyncQueue.enqueue(
      () => Sentry.startSpan(spanOpts, process),
      action.payload.jid,
    )
  },
})

export default roomSyncListener
